import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import JSBI from 'jsbi';
import { useEffect } from 'react';

import { fetchMarketTicker, fetchQuoteTickers } from 'api/apiExchange';
import { KatanaApiError } from 'api/apiPatchConstants';
import {
  startPublicSubscription,
  stopSubscription,
} from 'api/services/webSocketApiService';
import { useWebsocketEventListener } from 'components/hooks';
import {
  API_ERROR_MARKET_NOT_FOUND,
  RELOAD_MARKETS_TICKERS_DELAY_MS,
} from 'config/constants';
import { parseMarketSymbol } from 'lib/exchange';
import { decimalToPip } from 'lib/numbers';
import { useSelector } from 'reduxState/store';

import { minutes, seconds } from './utils';

import type { ApiTicker } from 'api/types';

export interface StateTicker extends Omit<ApiTicker, 'percentChange'> {
  baseSymbol: string;
  quoteSymbol: string;
  baseVolumePip: JSBI;
  quoteVolumePip: JSBI;
  percentChange: number;
  /** Frontend calculated USD open interest (interest * price) */
  openInterestInQuote: number;
}

export type StateTickersMap = {
  [market: string]: StateTicker;
};

function transformApiTicker(
  ticker: ApiTicker,
  fallbackLastFillPrice?: string | null
): StateTicker {
  const market = parseMarketSymbol(ticker.market);
  return {
    ...ticker,
    percentChange: Number(ticker.percentChange),
    close: ticker.close || fallbackLastFillPrice || null,
    low: ticker.low,
    high: ticker.high,
    baseVolumePip: decimalToPip(ticker.baseVolume),
    quoteVolumePip: decimalToPip(ticker.quoteVolume),
    openInterestInQuote: ticker.openInterest
      ? Number(ticker.openInterest) * Number(ticker.indexPrice)
      : 0,
    baseSymbol: market.baseSymbol,
    quoteSymbol: market.quoteSymbol,
  };
}

const isSymbolValid = (symbol: string) => {
  return symbol && symbol.length >= 1 && symbol.length <= 8;
};

const isMarketValid = (market: string) => {
  if (!market) {
    return false;
  }
  const [base, quote] = market.split('-');
  return isSymbolValid(base) && isSymbolValid(quote);
};

/**
 * Helper to prevent unsubscribing active tickers on some transitions eg.
 *  - listening to current market (dashboard) and all tickers (sidebar)
 *  - after closing sidebar, we still want to keep current one subsc
 */
const subscriptionController = {
  /** Tickers sidebar or Markets page */
  all: false,
  /** Active per market subscription list - NOTE: could have market multiple times */
  activeList: [] as string[],
  /** Add single item to list (can add duplicities) */
  push: (market: string) => {
    subscriptionController.activeList.push(market);
  },
  /** Remove single item from list (could still exist after if was added twice) */
  pop: (market: string) => {
    const index = subscriptionController.activeList.indexOf(market);
    if (index > -1) {
      subscriptionController.activeList.splice(index, 1);
    }
  },
};

const fetchCurrentTicker = async (currentMarket: string) => {
  try {
    const marketTicker = await fetchMarketTicker(currentMarket);
    if (!marketTicker) {
      return false;
    }
    if (marketTicker.market !== currentMarket) {
      console.error('[api:ticker] Unexpected market returned');
      return;
    }
    return marketTicker;
  } catch (error) {
    if (
      (error as KatanaApiError)?.idexErrorCode === API_ERROR_MARKET_NOT_FOUND
    ) {
      return false;
    }
    console.warn(error);
  }
};

export const useGlobalTickersQueriesInvalidator = () => {
  const queryClient = useQueryClient();
  // Reload of quote tickers list on "reload_markets" event
  useWebsocketEventListener('webclient', message => {
    if (
      message.data.event === 'exchange_status_updated' &&
      message.data.action?.includes('market')
    ) {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['tickers'],
        });
      }, RELOAD_MARKETS_TICKERS_DELAY_MS);
    }
  });
};

const useMarketTickerQueryWSSync = (market: string | undefined | false) => {
  const queryClient = useQueryClient();
  const websocketConnected = useSelector(
    state => state.exchange.websocketState.connected
  );
  useEffect(() => {
    if (!market || !websocketConnected) {
      return;
    }
    subscriptionController.push(market);
    if (subscriptionController.all) {
      console.log('[tickersQueries] Single market - already subcribed all');
    } else {
      startPublicSubscription({ name: 'tickers', markets: [market] });
    }
    return () => {
      subscriptionController.pop(market);
      if (
        !subscriptionController.all &&
        subscriptionController.activeList.indexOf(market) > -1
      ) {
        // All subscription will unsubscribe instead
        stopSubscription({ name: 'tickers', markets: [market] });
      }
    };
  }, [market, websocketConnected]);

  useWebsocketEventListener('tickers', event => {
    const data = event?.data;
    if (!data.market) {
      console.error('[ws:tickers] Missing market');
    }
    if (data.market !== market) {
      return;
    }
    queryClient.setQueryData<StateTicker>(['tickers', data.market], () =>
      transformApiTicker(data)
    );
  });
};

export const useMarketTickerQuery = ({
  market,
  websocketSubscribe,
}: {
  market?: string;
  websocketSubscribe: boolean;
}) => {
  useMarketTickerQueryWSSync(websocketSubscribe ? market : undefined);
  return useQuery<StateTicker | false>({
    queryKey: ['tickers', market],
    queryFn: !market
      ? skipToken
      : async () => {
          if (!isMarketValid(market)) {
            return false; // Invalid market
          }
          const data = await fetchCurrentTicker(market);
          if (data === false) {
            // Market not found
            return false;
          }
          if (!data) {
            throw new Error(`Getting ${market} market error`);
          }
          return transformApiTicker(data);
        },
    // Small stale time - it should be updated by WebSocket live
    staleTime: seconds(7),
    refetchOnWindowFocus: false,
    refetchInterval: minutes(2),
  });
};

const fetchAllTickers = async () => {
  const allTickers = await fetchQuoteTickers('USD');
  const tickersMap = allTickers.reduce((map, ticker) => {
    map[ticker.market] = transformApiTicker(ticker);
    return map;
  }, {} as StateTickersMap);
  return tickersMap;
};

/** (Desktop Only) Prefetch tickers once so data are ready once user click to open sidebar */
export const usePreFetchTickersOnce = (enabled: boolean) => {
  return useQuery<StateTickersMap>({
    queryKey: ['tickers', 'all-markets'],
    queryFn: fetchAllTickers,
    enabled,
    // Small stale time - it should be updated by WebSocket live for active markets
    staleTime: minutes(20),
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

export const useTickersQuery = () => {
  const queryClient = useQueryClient();
  const listedMarketsList = useSelector(
    state => state.markets.listedMarkets.listedMarketsList
  );
  const websocketConnected = useSelector(
    state => state.exchange.websocketState.connected
  );
  useEffect(() => {
    if (!websocketConnected) {
      return;
    }
    subscriptionController.all = true;
    startPublicSubscription({ name: 'tickers', markets: listedMarketsList });
    return () => {
      subscriptionController.all = false;
      const unsubscribeFrom = listedMarketsList?.filter(
        market => subscriptionController.activeList.indexOf(market) === -1
      );
      if (unsubscribeFrom && unsubscribeFrom.length > 0) {
        stopSubscription({
          name: 'tickers',
          markets: unsubscribeFrom,
        });
      }
    };
  }, [websocketConnected, listedMarketsList]);

  useWebsocketEventListener('tickers', event => {
    const data = event?.data;
    if (!data?.market) {
      console.error('[ws:tickers] Missing market');
    }
    const queryData = queryClient.getQueryState(['tickers', 'all-markets']);
    queryClient.setQueryData<StateTickersMap>(
      ['tickers', 'all-markets'],
      existingMapOrUndefined => {
        if (!existingMapOrUndefined) {
          // Wait for first API data before collecting events
          return undefined;
        }
        const existingMap = existingMapOrUndefined || {};
        return {
          ...existingMap,
          [data.market]: transformApiTicker(data),
        };
      },
      // For staleTime to work we have to leave this unchanged
      { updatedAt: queryData?.dataUpdatedAt || Date.now() }
    );
  });

  return useQuery<StateTickersMap>({
    queryKey: ['tickers', 'all-markets'],
    queryFn: fetchAllTickers,
    enabled: true,
    // Small stale time - it should be updated by WebSocket live for active markets
    staleTime: seconds(7),
    refetchOnWindowFocus: false,
    refetchInterval: minutes(1),
  });
};
